import { t } from '@lingui/core/macro';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';
import { useCreateMutation } from 'api';
import { CodeInput } from 'components/CodeInput/CodeInput';
import { BasicModal } from 'components/modals/BasicModal';
import { type FC, useState } from 'react';
import { toast } from 'react-toastify';
import type {
  AuthenticateWithCodeError,
  InitiateOtpAuthenticationError,
  StrongAuthenticationChannel,
} from 'types/api/strong_authentication';

type ErrorReason = InitiateOtpAuthenticationError | AuthenticateWithCodeError;

type Step =
  | { name: 'intro' }
  | {
      name: 'code';
      challengeToken: string;
      channel: StrongAuthenticationChannel;
      destination: string;
    };

const CODE_LENGTH = 6;

const reasonToMessage = (reason: ErrorReason) => {
  switch (reason) {
    case 'invalid_code':
      return t`Le code n’est pas valide.`;
    case 'no_otp_destination':
      return t`Aucun numéro de téléphone vérifié ni adresse e-mail valide n’est associé à votre compte. Ajoutez-en un pour pouvoir confirmer cette action.`;
    case 'invalid_strong_authentication_session':
      return t`L’authentification a échoué. Merci de réessayer.`;
  }
};

const sentCodeMessage = (channel: StrongAuthenticationChannel, destination: string) => {
  switch (channel) {
    case 'sms':
      return t`Entrez le code reçu par SMS au ${destination}.`;
    case 'email':
      return t`Entrez le code reçu par e-mail à ${destination}.`;
    default: {
      const _exhaustive: never = channel;
      return t`Entrez le code reçu.`;
    }
  }
};

type Props = {
  onClose: VoidFunction;
  onAuthenticated: VoidFunction;
};

export const OtpAuthenticationModal: FC<Props> = ({ onClose, onAuthenticated }) => {
  const [step, setStep] = useState<Step>({ name: 'intro' });
  const [code, setCode] = useState('');
  const [error, setError] = useState<ErrorReason | null>(null);

  const initiateMutation = useCreateMutation('tech/strong_authentication/initiate_with_otp');
  const authenticateMutation = useCreateMutation(
    'tech/strong_authentication/authenticate_with_code'
  );

  const onSendCode = async () => {
    setError(null);
    try {
      const response = await initiateMutation.mutateAsync({});
      if (!response.success) {
        setError(response.reason);
        return;
      }
      setStep({
        name: 'code',
        challengeToken: response.challenge_token,
        channel: response.channel,
        destination: response.destination,
      });
    } catch {
      setError('invalid_strong_authentication_session');
    }
  };

  const onSubmitCode = async () => {
    if (step.name !== 'code') return;

    setError(null);
    try {
      const response = await authenticateMutation.mutateAsync({
        challenge_token: step.challengeToken,
        code,
      });

      if (!response.success) {
        setError(response.reason);
        if (response.reason === 'invalid_code') {
          toast.error(reasonToMessage(response.reason));
          setCode('');
        }
        return;
      }

      onAuthenticated();
    } catch {
      setError('invalid_strong_authentication_session');
    }
  };

  return (
    <BasicModal open onClose={onClose} title={t`Confirmation par code`}>
      <Stack spacing={3}>
        {error && error !== 'invalid_code' && (
          <Typography color="error">{reasonToMessage(error)}</Typography>
        )}

        {step.name === 'intro' && (
          <>
            <Typography>
              {t`Cette action sensible doit être confirmée par un code à usage unique, envoyé par SMS ou par e-mail.`}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="contained" color="inherit" onClick={onClose}>
                {t`Annuler`}
              </Button>
              <LoadingButton
                variant="contained"
                onClick={onSendCode}
                loading={initiateMutation.isPending}
              >
                {t`Recevoir un code`}
              </LoadingButton>
            </Stack>
          </>
        )}

        {step.name === 'code' && (
          <Stack spacing={3} alignItems="center">
            <Typography textAlign="center">
              {sentCodeMessage(step.channel, step.destination)}
            </Typography>
            <CodeInput
              value={code}
              onChange={setCode}
              length={CODE_LENGTH}
              isError={error === 'invalid_code'}
            />
            <LoadingButton
              variant="contained"
              onClick={onSubmitCode}
              loading={authenticateMutation.isPending}
              disabled={code.length < CODE_LENGTH}
            >
              {t`Valider`}
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </BasicModal>
  );
};
